import { isPlatformBrowser } from '@angular/common';
import { inject, PLATFORM_ID, Service } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

@Service()
export class GoogleAnalyticsService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private trackingId: string | null = null;
  private isInitialized = false;

  /**
   * Initialize GA tracking and subscribe to router page view changes
   */
  public init(trackingId: string): void {
    if (!isPlatformBrowser(this.platformId) || !trackingId || this.isInitialized) {
      return;
    }

    this.trackingId = trackingId;
    this.isInitialized = true;

    // Listen to router events to send automatic pageview hits
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.trackPageView(event.urlAfterRedirects);
      });
  }

  /**
   * Track a page view manually
   */
  public trackPageView(pageUrl: string, pageTitle?: string): void {
    if (!isPlatformBrowser(this.platformId) || !window.gtag || !this.trackingId) {
      return;
    }

    window.gtag('config', this.trackingId, {
      page_path: pageUrl,
      page_title: pageTitle || (typeof document !== 'undefined' ? document.title : ''),
    });
  }

  /**
   * Track custom GA4 events
   */
  public trackEvent(eventName: string, eventParams: Record<string, unknown> = {}): void {
    if (!isPlatformBrowser(this.platformId) || !window.gtag) {
      return;
    }

    window.gtag('event', eventName, eventParams);
  }
}
